/**
 * Utility functions for culture-invariant number and vector parsing/formatting.
 * These functions ensure consistent behavior regardless of user locale settings.
 */

import { captureError } from '@/utils/sentry';

/**
 * Parses a string to a float in a culture-invariant way.
 * Handles:
 * - Comma as decimal separator (European locales)
 * - Period as decimal separator (US/UK locales)
 * - Thousands separators (both comma and period)
 * 
 * @param value - The string value to parse
 * @returns The parsed number, or NaN if invalid
 */
export const parseInvariantFloat = (value: string): number => {
  if (!value || typeof value !== 'string') {
    return NaN;
  }

  // Remove whitespace
  let normalized = value.trim().replace(/\s/g, '');
  
  // Handle negative sign
  const isNegative = normalized.startsWith('-');
  if (isNegative) {
    normalized = normalized.substring(1);
  }
  
  // Count commas and periods to determine which is the decimal separator
  const commaCount = (normalized.match(/,/g) || []).length;
  const periodCount = (normalized.match(/\./g) || []).length;
  
  // Only remove thousands separators if there are multiple of the same separator
  // AND there's another separator acting as decimal
  // e.g., "1,234,567.89" - commas are thousands separators
  // e.g., "1.234.567,89" - periods are thousands separators
  if (commaCount > 1 && periodCount <= 1) {
    // Multiple commas = commas are thousands separators
    normalized = normalized.replace(/,/g, '');
  } else if (periodCount > 1 && commaCount <= 1) {
    // Multiple periods = periods are thousands separators  
    normalized = normalized.replace(/\./g, '');
  }
  
  // Determine decimal separator by checking which appears last
  const lastComma = normalized.lastIndexOf(',');
  const lastPeriod = normalized.lastIndexOf('.');
  
  if (lastComma > lastPeriod) {
    // Comma is the decimal separator (European format: 1234,56)
    normalized = normalized.replace(',', '.');
  } else if (lastPeriod > lastComma) {
    // Period is the decimal separator (US format: 1234.56)
    // Already correct, but ensure no remaining commas
    normalized = normalized.replace(',', '');
  } else if (lastComma === -1 && lastPeriod === -1) {
    // No decimal separator, integer
    // Remove any remaining commas/periods (shouldn't be any, but safe)
    normalized = normalized.replace(/[,.]/g, '');
  }
  
  // Parse the normalized value
  const result = parseFloat(normalized);
  
  // Only log a genuinely mishandled number (a digit + numeric chars only); skip mid-edit "." / "-" / text.
  const looksLikeNumber = /\d/.test(value) && /^-?[\d.,]+$/.test(value.trim().replace(/\s/g, ''));
  if (isNaN(result) && looksLikeNumber) {
    captureError(
      new Error('parseInvariantFloat resulted in NaN for seemingly valid input'),
      {
        domain: 'invariant-protection',
        operation: 'parseInvariantFloat',
        inputValue: value,
        normalizedValue: normalized,
        detectedFormat: lastComma > lastPeriod ? 'european' : lastPeriod > lastComma ? 'us' : 'integer',
      }
    );
  }
  
  return isNegative ? -result : result;
};

/**
 * Parses a vector3 string (format: "x,y,z") to an object.
 * Handles culture-invariant parsing of each component.
 * Supports both US format (1.5,2.5,3.5) and European format (1,5,2,5,3,5).
 * 
 * @param vectorString - The vector string to parse (e.g., "1.5,2.5,3.5" or "1,5,2,5,3,5")
 * @returns The parsed vector object, or null if invalid
 */
export const parseVector3String = (vectorString: string): { x: number; y: number; z: number } | null => {
  if (!vectorString || typeof vectorString !== 'string') {
    return null;
  }
  
  const trimmed = vectorString.trim();
  if (trimmed === '') {
    return null;
  }
  
  // Split by comma
  const rawParts = trimmed.split(',').map(part => part.trim());
  
  // Handle European format where comma is both decimal separator and component separator
  // Example: "1,5,2,5,3,5" means x=1.5, y=2.5, z=3.5
  // This results in 6 parts when split by comma
  if (rawParts.length === 6) {
    // Validate that this is actually European format before pairing
    // Even indices (0, 2, 4) should be integer parts (can have negative sign)
    // Odd indices (1, 3, 5) should be decimal parts (digits only, no negative sign)
    const isValidEuropeanFormat = rawParts.every((part, index) => {
      if (index % 2 === 0) {
        // Integer part - should be a valid integer (can have negative sign)
        return /^-?\d+$/.test(part);
      } else {
        // Decimal part - should be digits only (no negative sign, no decimal point)
        return /^\d+$/.test(part);
      }
    });
    
    if (isValidEuropeanFormat) {
      // Try pairing up parts: [1,5] -> 1.5, [2,5] -> 2.5, [3,5] -> 3.5
      const paired: string[] = [];
      for (let i = 0; i < rawParts.length; i += 2) {
        if (i + 1 < rawParts.length) {
          paired.push(`${rawParts[i]}.${rawParts[i + 1]}`);
        }
      }
      
      if (paired.length === 3) {
        const parts = paired.map(part => parseInvariantFloat(part));
        if (!parts.some(isNaN)) {
          return { x: parts[0], y: parts[1], z: parts[2] };
        } else {
          // European format validation passed but parsing failed - track this edge case
          captureError(
            new Error('European format vector3 validation passed but parsing failed'),
            {
              domain: 'invariant-protection',
              operation: 'parseVector3String',
              inputValue: vectorString,
              parts: rawParts,
              pairedParts: paired,
              parsedParts: parts,
              format: 'european-6-parts',
            }
          );
        }
      }
    } else {
      // 6 parts but doesn't match European format pattern - track for monitoring
      captureError(
        new Error('Vector3 has 6 parts but failed European format validation'),
        {
          domain: 'invariant-protection',
          operation: 'parseVector3String',
          inputValue: vectorString,
          parts: rawParts,
          format: 'invalid-european-6-parts',
          note: 'Will attempt fallback parsing',
        }
      );
    }
  }
  
  // Handle standard format (3 components separated by comma)
  // Examples: "1.5,2.5,3.5" or "1,5,2,5,3,5" (if already normalized)
  if (rawParts.length === 3) {
    const parts = rawParts.map(part => parseInvariantFloat(part));
    
    if (parts.some(isNaN)) {
      // Track parsing failures to identify edge cases and invalid formats
      captureError(
        new Error('Invalid vector3 string (contains NaN)'),
        {
          domain: 'invariant-protection',
          operation: 'parseVector3String',
          inputValue: vectorString,
          parts: rawParts,
          parsedParts: parts,
          format: 'standard-3-parts',
        }
      );
      console.warn('Invalid vector3 string (contains NaN):', vectorString);
      return null;
    }
    
    return { x: parts[0], y: parts[1], z: parts[2] };
  }
  
  // If we have a different number of parts, try to parse as-is
  // This handles edge cases like "1.5, 2.5, 3.5" with spaces
  if (rawParts.length > 3) {
    // Try to parse first 3 valid numbers
    const validParts: number[] = [];
    for (const part of rawParts) {
      const parsed = parseInvariantFloat(part);
      if (!isNaN(parsed)) {
        validParts.push(parsed);
        if (validParts.length === 3) break;
      }
    }
    
    if (validParts.length === 3) {
      // Successfully parsed from >3 parts - track this edge case for monitoring
      // Using captureError to track these cases, but note it's a successful parse
      captureError(
        new Error('Vector3 parsed from unexpected number of parts (successful fallback)'),
        {
          domain: 'invariant-protection',
          operation: 'parseVector3String',
          inputValue: vectorString,
          partsCount: rawParts.length,
          parts: rawParts,
          parsedParts: validParts,
          format: 'fallback-multi-part',
          note: 'This is a successful parse from an unexpected format - monitoring for edge cases',
        }
      );
      return { x: validParts[0], y: validParts[1], z: validParts[2] };
    }
  }
  
  // Failed to parse - track for monitoring edge cases
  captureError(
    new Error('Invalid vector3 string format (expected 3 components)'),
    {
      domain: 'invariant-protection',
      operation: 'parseVector3String',
      inputValue: vectorString,
      partsCount: rawParts.length,
      parts: rawParts,
      format: 'unparseable',
    }
  );
  console.warn('Invalid vector3 string format (expected 3 components):', vectorString);
  return null;
};

/**
 * Formats a vector3 object to a string (format: "x,y,z").
 * Always uses period as decimal separator for consistency.
 * 
 * @param vector - The vector object to format
 * @param precision - Number of decimal places (default: 3)
 * @returns The formatted string (e.g., "1.500,2.500,3.500")
 */
export const formatVector3String = (
  vector: { x: number; y: number; z: number },
  precision: number = 3
): string => {
  const formatComponent = (value: number): string => {
    // Round to specified precision
    const rounded = Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
    // Format with period as decimal separator
    return rounded.toFixed(precision);
  };
  
  return `${formatComponent(vector.x)},${formatComponent(vector.y)},${formatComponent(vector.z)}`;
};

/**
 * Normalizes user input for number fields.
 * Converts comma to period and removes thousands separators.
 * 
 * @param inputValue - The raw input string from the user
 * @returns The normalized string ready for parsing
 */
export const normalizeNumberInput = (inputValue: string): string => {
  if (!inputValue || typeof inputValue !== 'string') {
    return '';
  }
  
  // Remove whitespace
  let normalized = inputValue.trim().replace(/\s/g, '');
  
  // Handle negative sign
  const isNegative = normalized.startsWith('-');
  if (isNegative) {
    normalized = normalized.substring(1);
  }
  
  // Count commas and periods to determine which is the decimal separator
  const commaCount = (normalized.match(/,/g) || []).length;
  const periodCount = (normalized.match(/\./g) || []).length;
  
  // Only remove thousands separators if there are multiple of the same separator
  if (commaCount > 1 && periodCount <= 1) {
    normalized = normalized.replace(/,/g, '');
  } else if (periodCount > 1 && commaCount <= 1) {
    normalized = normalized.replace(/\./g, '');
  }
  
  // Determine decimal separator
  const lastComma = normalized.lastIndexOf(',');
  const lastPeriod = normalized.lastIndexOf('.');
  
  if (lastComma > lastPeriod) {
    // Comma is decimal separator, convert to period
    normalized = normalized.replace(/,/g, '.');
  } else if (lastPeriod > lastComma) {
    // Period is decimal separator, remove any remaining commas
    normalized = normalized.replace(/,/g, '');
  } else {
    // No decimal separator, remove all commas/periods
    normalized = normalized.replace(/[,.]/g, '');
  }
  
  return isNegative ? '-' + normalized : normalized;
};
